"use client"

import { useCallback } from 'react'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useAgentConnection } from '@/entities/realtime-agent/model/use-agent-connection'
import { resetGlobalListeners, forceRegisterListeners } from '@/entities/realtime-agent/model/use-agent-messages'
import { toastActions } from '../toast'
import type { useChatModeController } from '@/features/avatar/manage-chat-modes/model/use-chat-mode-controller'
import type { useConsentController } from '@/features/avatar/manage-consent/model/use-consent-controller'

interface UseDisconnectionHandlerParams {
  consentController: ReturnType<typeof useConsentController>
  chatModeController: ReturnType<typeof useChatModeController>
  pauseAudio: () => void
}

/**
 * 서버 연결 해제 처리 훅
 * disconnect_notice 이벤트 발생 시 재연결 로직 제공
 */
export const useDisconnectionHandler = ({
  consentController,
  chatModeController,
  pauseAudio,
}: UseDisconnectionHandlerParams) => {
  const { manualRetry } = useAgentConnection()

  /**
   * 재연결 처리
   * 1. 모달 및 상태 초기화
   * 2. 토스트 정리
   * 3. 오디오 정지
   * 4. 상태 초기화
   * 5. 동의 상태 리셋 → ConsentModal 표시
   * 6. 채팅 모드 초기화
   * 7. 전역 리스너 상태 리셋
   * 8. WebSocket 재연결
   */
  const handleReconnect = useCallback(async () => {
    const store = useRealtimeAgentStore.getState()

    console.log('🔄 [RECONNECT] 재연결 시작...')

    // 1. 재연결 모달 닫기
    store.setShowReconnectModal(false)
    store.setDisconnectedByServer(false, null)

    // 2. 토스트 정리
    toastActions.clearAll()

    // 3. 오디오 정지
    pauseAudio()

    // 4. 상태 초기화 (handleNavigateToMode(null) 로직과 동일)
    store.clearCurrentTurn()
    store.setMessages([])
    store.setAvatarSetupComplete(false)

    // 5. 동의 상태 리셋 → ConsentModal 표시
    consentController.resetConsent()

    // 6. 채팅 모드 초기화
    chatModeController.switchToVoiceMode()

    // 7. 전역 리스너 상태 리셋 (새 API에 리스너 재등록 위해)
    resetGlobalListeners()

    // 8. WebSocket 재연결
    try {
      await manualRetry()
      console.log('✅ [RECONNECT] 재연결 성공')

      // 9. 리스너 강제 재등록 (React 상태 배칭으로 useEffect가 실행 안 될 수 있음)
      // - isConnected가 true → false → true로 변경되면 React가 배칭하여 변화를 감지 못할 수 있음
      // - 직접 호출하여 리스너 등록 보장
      forceRegisterListeners()
    } catch (error) {
      console.error('❌ [RECONNECT] 재연결 실패:', error)
      toastActions.show({
        type: 'error',
        message: '재연결에 실패했습니다. 잠시 후 다시 시도해주세요.',
        position: 'top',
        duration: 5000,
      })
    }
  }, [consentController, chatModeController, pauseAudio, manualRetry])

  return { handleReconnect }
}
